import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  inject,
  input,
  OnChanges,
  output,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { StoresService } from '@components/stores-locator/services';
import { Cluster, MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { mapConfig, mapOptions } from '@shared/components/map/config';
import { CustomerRendererCluster, Feature } from '@shared/components/map/model';
import { getMarker } from '@shared/components/map/utils/get-marker';
import LatLng = google.maps.LatLng;
import LatLngBounds = google.maps.LatLngBounds;

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnChanges {
  readonly mapElement = viewChild.required<ElementRef<HTMLElement>>('mapElement');
  map!: google.maps.Map;
  readonly isClustered = input(true);
  readonly zoom = input(6);
  readonly forceZoomOnFeature = input(false);
  readonly center = input<google.maps.LatLng>();
  readonly features = input<Feature[]>([]);
  readonly style = input<{
    width?: string;
    height?: string;
  }>({
    height: '70rem',
    width: '100%',
  });
  readonly clusterClick = output<Cluster>();
  readonly markerClick = output<google.maps.Marker>();
  markers: google.maps.Marker[] = [];
  markerClusterer!: MarkerClusterer;
  readonly #window = inject(DOCUMENT).defaultView as Window;
  readonly #storesService = inject(StoresService);
  private firstInit = true;

  constructor() {
    afterNextRender(() => {
      this.map = new google.maps.Map(
        this.mapElement().nativeElement,
        mapOptions(new google.maps.LatLng(this.center() ?? mapConfig.center), this.zoom())
      );
      this.map.addListener('idle', () => {
        this.setVisibleStores();
      });
      this.createMarkers();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['features'] && !changes['features'].firstChange) {
      this.firstInit = false;
      this.clearMarkers();
      this.createMarkers();
    }
  }

  onZoomIn(): void {
    const zoom = this.map.getZoom();
    if (zoom !== undefined) this.map.setZoom(zoom + 1);
  }

  onZoomOut(): void {
    const zoom = this.map.getZoom();
    if (zoom !== undefined) this.map.setZoom(zoom - 1);
  }

  private createMarkers(): void {
    this.markers = this.features().map((feature) => {
      const marker = this.isClustered()
        ? getMarker(this.#window, feature.position)
        : getMarker(this.#window, feature.position, this.map);
      marker.set('uuid', feature.uuid);
      marker.addListener('click', () => {
        this.map.panTo(marker.getPosition() as LatLng);
        this.map.setZoom(mapConfig.zoom);
        this.markerClick.emit(marker);
      });

      return marker;
    });
    if (this.isClustered()) {
      this.markerClusterer = new MarkerClusterer({
        markers: this.markers.map((marker) => marker),
        map: this.map,
        algorithm: new SuperClusterAlgorithm({ radius: 150 }),
        renderer: new CustomerRendererCluster(this.#window),
        onClusterClick: (_: google.maps.MapMouseEvent, cluster: Cluster, map: google.maps.Map) => {
          if (cluster.bounds !== undefined) map.fitBounds(cluster.bounds, 0);
          this.clusterClick.emit(cluster);
        },
      });
    }
    if (!this.firstInit || this.forceZoomOnFeature()) {
      this.zoomOnFeatures();
    }
  }

  private zoomOnFeatures() {
    const bounds = new google.maps.LatLngBounds();
    for (const marker of this.markers) {
      const lat = marker.getPosition()?.lat();
      const lng = marker.getPosition()?.lng();
      if (lat !== undefined && lng !== undefined) {
        bounds.extend(new google.maps.LatLng(lat, lng));
      }
    }
    this.map.fitBounds(bounds);

    const maxZoomLevel = mapConfig.zoom;
    google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
      const mapZoom = this.map.getZoom();
      if (mapZoom && mapZoom > maxZoomLevel) {
        this.map.setZoom(maxZoomLevel);
      }
    });
  }

  private setVisibleStores() {
    const storeUuid: string[] = this.markers.map((marker) => {
      if ((this.map.getBounds() as LatLngBounds).contains(marker.getPosition() as LatLng)) {
        return marker.get('uuid') as string;
      }
      return '';
    });
    this.#storesService.setStoresVisible(new Set<string>(storeUuid));
  }

  private clearMarkers(): void {
    this.markerClusterer.clearMarkers();
    this.markerClusterer.setMap(null);
  }
}
